import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TERMINOLOGY_CACHE_CONFIG, TerminologyCacheItemConfig } from './terminology-cache.config';

interface PersistedTerminologyEnvelope {
  canonicalKey: string;
  fhirPath: string;
  itemVersion: string;
  cachedAt: number;
  status: number;
  statusText: string;
  body: any;
}

@Injectable({
  providedIn: 'root'
})
export class CacheStorageService {
  cache: any = {};

  private readonly bucketName = `${TERMINOLOGY_CACHE_CONFIG.storagePrefix}-${TERMINOLOGY_CACHE_CONFIG.cacheVersion}`;
  private readonly staleKeys = new Set<string>();
  private readonly revalidatingKeys = new Set<string>();
  private readonly hydrationPromise: Promise<void>;

  constructor() {
    this.hydrationPromise = this.hydrateFromBrowserCacheStorage();
  }

  /**
   * Resolves a canonical cache key for URLs matching `TERMINOLOGY_CACHE_CONFIG.items`,
   * stripping volatile interceptor params like `_sort` and `_count` and base URL differences
   * so `/ValueSet/nafdac-medications` and `/ValueSet/nafdac-medications?_sort=-_lastUpdated`
   * always resolve to the exact same cache key.
   *
   * For non-terminology URLs, returns `rawUrl` unchanged.
   */
  getCacheKey(rawUrl: string): string {
    const match = this.matchTerminologyConfig(rawUrl);
    if (!match) {
      return rawUrl;
    }
    return this.buildCanonicalKey(match.normalizedPathWithQuery, match.config);
  }

  /**
   * Synchronous cache lookup used by loadingInterceptor.
   */
  getCachedResponse(rawUrl: string): HttpResponse<any> | null {
    const key = this.getCacheKey(rawUrl);
    if (Object.prototype.hasOwnProperty.call(this.cache, key)) {
      const cached = this.cache[key];
      return new HttpResponse({ ...cached, url: rawUrl });
    }
    if (Object.prototype.hasOwnProperty.call(this.cache, rawUrl)) {
      const cached = this.cache[rawUrl];
      return new HttpResponse({ ...cached, url: rawUrl });
    }
    return null;
  }

  /**
   * Returns true if a cached terminology entry is older than its configured TTL
   * and should be silently refreshed in the background (stale-while-revalidate).
   */
  shouldRevalidateInBackground(rawUrl: string): boolean {
    const key = this.getCacheKey(rawUrl);
    if (!this.staleKeys.has(key)) return false;
    if (this.revalidatingKeys.has(key)) return false;
    return true;
  }

  markRevalidating(rawUrl: string, inProgress: boolean): void {
    const key = this.getCacheKey(rawUrl);
    if (inProgress) {
      this.revalidatingKeys.add(key);
    } else {
      this.revalidatingKeys.delete(key);
    }
  }

  /**
   * Stores a GET HttpResponse in the in-memory cache and, if the URL matches
   * `TERMINOLOGY_CACHE_CONFIG.items`, persists it to the browser's `window.caches`.
   */
  setCachedResponse(rawUrl: string, response: HttpResponse<any>): void {
    const key = this.getCacheKey(rawUrl);
    this.cache[key] = response;
    this.cache[rawUrl] = response;
    this.staleKeys.delete(key);

    const match = this.matchTerminologyConfig(rawUrl);
    if (match && response.status >= 200 && response.status < 300 && response.body) {
      void this.persistToBrowserCacheStorage(key, match.normalizedPathWithQuery, match.config, response);
    }
  }

  /**
   * Waits for initial `window.caches` hydration to complete (useful before startup prefetching).
   */
  whenHydrated(): Promise<void> {
    return this.hydrationPromise;
  }

  /**
   * Silently prefetches any items in `TERMINOLOGY_CACHE_CONFIG.items` marked with
   * `prefetchOnStartup: true` if they are not already present in the cache.
   */
  async prefetchConfiguredTerminologies(
    fetchFn: (fhirPath: string) => void
  ): Promise<void> {
    await this.whenHydrated();
    const toPrefetch = TERMINOLOGY_CACHE_CONFIG.items.filter(
      item => item.prefetchOnStartup && !item.path.includes('*')
    );
    if (!toPrefetch.length) return;

    setTimeout(() => {
      for (const item of toPrefetch) {
        const testUrl = `/fhir/${item.path}`;
        if (!this.getCachedResponse(testUrl) || this.shouldRevalidateInBackground(testUrl)) {
          fetchFn(item.path);
        }
      }
    }, TERMINOLOGY_CACHE_CONFIG.prefetchDelayMs);
  }

  /**
   * Manually invalidate all cached GET responses for a specific FHIR resource type
   * @param resourceType e.g., 'Observation', 'Patient', 'ValueSet'
   */
  clearCacheForResource(resourceType: string): void {
    Object.keys(this.cache).forEach(key => {
      // Matches basic FHIR search/read endpoints or canonical terminology keys for this resource
      if (
        key.includes(`/${resourceType}`) ||
        key.includes(`?_type=${resourceType}`) ||
        key.includes(`::${resourceType}/`)
      ) {
        delete this.cache[key];
      }
    });

    if (resourceType === 'ValueSet' || resourceType === 'CodeSystem') {
      void this.clearBrowserCacheForResourceType(resourceType);
    }
  }

  /**
   * Clear cached data.
   * By default, purges all in-memory tenant/session data while preserving global persistent
   * terminologies (like NAFDAC medications) in browser Cache Storage across logouts and hospital switches.
   * Pass `clearPersistentStorage = true` to explicitly delete the browser Cache Storage bucket.
   */
  clearAll(clearPersistentStorage: boolean = false): void {
    this.cache = {};
    this.staleKeys.clear();
    if (clearPersistentStorage && typeof window !== 'undefined' && 'caches' in window) {
      void window.caches.delete(this.bucketName);
    } else {
      void this.hydrateFromBrowserCacheStorage();
    }
  }

  private buildCanonicalKey(normalizedPathWithQuery: string, itemConfig: TerminologyCacheItemConfig): string {
    const itemVersion = itemConfig.version || 'v1';
    return `terminology::${TERMINOLOGY_CACHE_CONFIG.cacheVersion}::${normalizedPathWithQuery}::${itemVersion}`;
  }

  /**
   * Extracts the FHIR resource path (e.g. `ValueSet/nafdac-medications`) and checks if it
   * matches any entry in `TERMINOLOGY_CACHE_CONFIG.items`.
   */
  matchTerminologyConfig(rawUrl: string): {
    fhirPath: string;
    normalizedPathWithQuery: string;
    config: TerminologyCacheItemConfig;
  } | null {
    if (!rawUrl) return null;

    let pathname = rawUrl;
    let search = '';
    try {
      const parsed = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      pathname = parsed.pathname;
      search = parsed.search;
    } catch {
      const qIdx = rawUrl.indexOf('?');
      if (qIdx >= 0) {
        pathname = rawUrl.slice(0, qIdx);
        search = rawUrl.slice(qIdx);
      }
    }

    // Extract the segment after `/fhir/` (or use the last 2 segments if `/ValueSet/...` or `/CodeSystem/...`)
    let fhirPath = '';
    const fhirMarkerMatch = pathname.match(/\/fhir\/(.+)$/i);
    if (fhirMarkerMatch && fhirMarkerMatch[1]) {
      fhirPath = fhirMarkerMatch[1].replace(/^\/+|\/+$/g, '');
    } else {
      const termMatch = pathname.match(/\/((?:ValueSet|CodeSystem)\/[^/?#]+)$/i);
      if (termMatch && termMatch[1]) {
        fhirPath = termMatch[1].replace(/^\/+|\/+$/g, '');
      }
    }

    if (!fhirPath) return null;

    // Prefer exact path match first, then wildcard match
    let matchedConfig = TERMINOLOGY_CACHE_CONFIG.items.find(
      item => !item.path.includes('*') && item.path.toLowerCase() === fhirPath.toLowerCase()
    );

    if (!matchedConfig) {
      matchedConfig = TERMINOLOGY_CACHE_CONFIG.items.find(item => {
        if (!item.path.endsWith('/*')) return false;
        const prefix = item.path.slice(0, -1).toLowerCase(); // e.g. 'valueset/'
        return fhirPath.toLowerCase().startsWith(prefix);
      });
    }

    if (!matchedConfig) return null;

    // Strip ignored query parameters (e.g. _sort, _count)
    const ignoredSet = new Set(
      (TERMINOLOGY_CACHE_CONFIG.ignoredQueryParams || ['_sort', '_count']).map(p => p.toLowerCase())
    );
    const cleanParams = new URLSearchParams();
    if (search) {
      const rawParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
      const sortedKeys = Array.from(new Set(Array.from(rawParams.keys()))).sort();
      for (const k of sortedKeys) {
        if (ignoredSet.has(k.toLowerCase())) continue;
        const vals = rawParams.getAll(k);
        for (const v of vals) {
          cleanParams.append(k, v);
        }
      }
    }

    const qs = cleanParams.toString();
    const normalizedPathWithQuery = qs ? `${fhirPath}?${qs}` : fhirPath;

    return {
      fhirPath,
      normalizedPathWithQuery,
      config: matchedConfig,
    };
  }

  private async hydrateFromBrowserCacheStorage(): Promise<void> {
    if (typeof window === 'undefined' || !('caches' in window)) {
      return;
    }

    try {
      // Clean up any older cache buckets if `TERMINOLOGY_CACHE_CONFIG.cacheVersion` was bumped
      const existingNames = await window.caches.keys();
      for (const name of existingNames) {
        if (name.startsWith(TERMINOLOGY_CACHE_CONFIG.storagePrefix) && name !== this.bucketName) {
          await window.caches.delete(name);
        }
      }

      const cacheBucket = await window.caches.open(this.bucketName);
      const requests = await cacheBucket.keys();

      for (const req of requests) {
        try {
          const resp = await cacheBucket.match(req);
          if (!resp) continue;
          const envelope = (await resp.json()) as PersistedTerminologyEnvelope;
          if (!envelope || !envelope.canonicalKey || !envelope.fhirPath || !envelope.body) {
            await cacheBucket.delete(req);
            continue;
          }

          // Verify item still matches current config & item version
          const currentMatch = this.matchTerminologyConfig(`/fhir/${envelope.fhirPath}`);
          if (!currentMatch) {
            await cacheBucket.delete(req);
            continue;
          }

          const expectedKey = this.buildCanonicalKey(
            currentMatch.normalizedPathWithQuery,
            currentMatch.config
          );
          if (envelope.canonicalKey !== expectedKey) {
            // Item version in terminology-cache.config.ts was bumped -> discard old cached copy!
            await cacheBucket.delete(req);
            continue;
          }

          // Hydrate into in-memory cache so synchronous interceptor lookups hit immediately (0ms wait)
          const hydratedResponse = new HttpResponse({
            body: envelope.body,
            status: envelope.status || 200,
            statusText: envelope.statusText || 'OK',
          });
          this.cache[expectedKey] = hydratedResponse;

          // Check TTL: if expired, mark for silent background revalidation (stale-while-revalidate)
          const ttlMs = currentMatch.config.ttlMs ?? TERMINOLOGY_CACHE_CONFIG.defaultTtlMs;
          if (Date.now() - (envelope.cachedAt || 0) > ttlMs) {
            this.staleKeys.add(expectedKey);
          }
        } catch {
          // Ignore malformed cache entries
        }
      }
    } catch (e) {
      console.warn('[CacheStorageService] Could not hydrate from window.caches:', e);
    }
  }

  private async persistToBrowserCacheStorage(
    canonicalKey: string,
    normalizedPathWithQuery: string,
    itemConfig: TerminologyCacheItemConfig,
    response: HttpResponse<any>
  ): Promise<void> {
    if (typeof window === 'undefined' || !('caches' in window)) {
      return;
    }

    try {
      const cacheBucket = await window.caches.open(this.bucketName);
      const syntheticUrl = `https://elikita-cache.local/${encodeURIComponent(canonicalKey)}`;
      const envelope: PersistedTerminologyEnvelope = {
        canonicalKey,
        fhirPath: normalizedPathWithQuery,
        itemVersion: itemConfig.version || 'v1',
        cachedAt: Date.now(),
        status: response.status || 200,
        statusText: response.statusText || 'OK',
        body: response.body,
      };

      const storedResponse = new Response(JSON.stringify(envelope), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      await cacheBucket.put(syntheticUrl, storedResponse);
    } catch (e) {
      console.warn('[CacheStorageService] Failed to persist terminology to window.caches:', e);
    }
  }

  private async clearBrowserCacheForResourceType(resourceType: string): Promise<void> {
    if (typeof window === 'undefined' || !('caches' in window)) {
      return;
    }
    try {
      const cacheBucket = await window.caches.open(this.bucketName);
      const requests = await cacheBucket.keys();
      for (const req of requests) {
        if (decodeURIComponent(req.url).includes(`::${resourceType}/`)) {
          await cacheBucket.delete(req);
        }
      }
    } catch {
      // Ignore storage errors
    }
  }
}
